import { getCountry, DEFAULT_COUNTRY } from "@/i18n/countries";

/**
 * Price formatting helpers driven by the visitor's current country.
 *
 * Perx's product prices are stored as a single number in the DB (no
 * multi-currency yet — Stripe still charges in the currency configured
 * on each Price object). These helpers ONLY change the visual display
 * so a Hong Kong visitor sees HK$1,000 while a Singapore visitor sees
 * S$1,000 — the underlying amount is untouched.
 *
 * FX conversion is deliberately out of scope. Bringing real per-market
 * pricing online requires:
 *   • One Stripe Price per (product × currency)
 *   • Nightly FX table for display parity, OR
 *   • Per-market retail prices stored on the deal row itself.
 *
 * These helpers are the CLEAN seam to bolt any of that on later —
 * bump the signature of `formatPrice` to accept the underlying amount
 * + its native currency, and callers keep working.
 */

// Currency symbols that Intl.NumberFormat doesn't render nicely in
// every locale (or renders too verbosely). Overrides win over Intl.
const SYMBOL_OVERRIDES = {
    HKD: "HK$",
    SGD: "S$",
    AUD: "A$",
    NZD: "NZ$",
    USD: "US$",
    TWD: "NT$",
};

/**
 * Format a number as a price string for the given country's currency.
 *
 * @param {number|null|undefined} amount
 * @param {string} countryCode - ISO alpha-2; defaults to HK.
 * @param {object} opts
 * @param {number} opts.decimals - fixed decimal places; auto if omitted.
 * @param {boolean} opts.compact - use K/M/B suffixes for large numbers.
 * @returns {string} formatted price, e.g. "HK$1,299" or "—" if null.
 */
export function formatPrice(amount, countryCode = DEFAULT_COUNTRY, opts = {}) {
    if (amount == null || Number.isNaN(Number(amount))) return "—";
    const currency = getCountry(countryCode).currency;
    const symbol = SYMBOL_OVERRIDES[currency] || currency;
    const value = Number(amount);
    const decimals = opts.decimals ?? (Number.isInteger(value) ? 0 : 2);

    if (opts.compact && Math.abs(value) >= 1000) {
        const nf = new Intl.NumberFormat("en", {
            notation: "compact",
            maximumFractionDigits: 1,
        });
        return `${symbol}${nf.format(value)}`;
    }

    const nf = new Intl.NumberFormat("en", {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    });
    return `${symbol}${nf.format(value)}`;
}

/**
 * Format a discount / delta. Same as `formatPrice` but with an explicit
 * sign prefix so admins can read at-a-glance whether a number is a
 * saving or a surcharge.
 */
export function formatDelta(amount, countryCode = DEFAULT_COUNTRY) {
    if (amount == null) return "—";
    const sign = amount >= 0 ? "−" : "+"; // "you save X" = we subtract from list
    return `${sign}${formatPrice(Math.abs(amount), countryCode)}`;
}

/**
 * Return just the currency symbol for a country. Handy for inputs that
 * already handle their own number formatting.
 */
export function currencySymbol(countryCode = DEFAULT_COUNTRY) {
    const currency = getCountry(countryCode).currency;
    return SYMBOL_OVERRIDES[currency] || currency;
}
